/**
 * QueryDef - Shared query definition for server/client
 *
 * Allows defining Supabase query logic once and using it in both:
 * - Client: useMany/useOne hooks
 * - Server: prefetchMany/prefetchOne functions
 *
 * This eliminates duplication of filter logic, SELECT columns, and parse functions.
 */

import type { SelectBuilder, TableName } from './query';

// ============ Default Constants ============

/** Default page size for list queries */
export const DEFAULT_PAGE_SIZE = 20;

/** Default stale time in ms (30 seconds) */
export const DEFAULT_STALE_TIME = 30 * 1000;

// ============ QueryDefMany ============

/**
 * Definition for list queries (useMany / prefetchMany)
 *
 * @template T - Table name
 * @template TRow - Raw row type from database
 * @template TDomain - Parsed domain type
 * @template TParams - Parameters for the query (e.g., { userId: string })
 */
export interface QueryDefMany<T extends TableName, TRow, TDomain, TParams = void> {
  /** Table name to query */
  tableName: T;

  /** Columns to select. Can be dynamic based on params. */
  columns: string | ((params: TParams) => string);

  /** Generate query key from params */
  queryKey: (params: TParams) => readonly unknown[];

  /**
   * Chain query methods after select (filter, order, etc.)
   * Range is applied automatically by useMany/prefetchMany.
   */
  trailingQuery: (query: SelectBuilder<T>, params: TParams) => SelectBuilder<T>;

  /** Transform Row to Domain type */
  parseFn: (data: TRow) => TDomain;

  /** Default page size (optional, default: 20) */
  pageSize?: number;

  /** Stale time in ms (optional, default: 30s) */
  staleTime?: number;
}

// ============ QueryDefOne ============

/**
 * Definition for single item queries (useOne / prefetchOne)
 *
 * @template T - Table name
 * @template TRow - Raw row type from database
 * @template TDomain - Parsed domain type
 * @template TParams - Parameters for the query
 */
export interface QueryDefOne<T extends TableName, TRow, TDomain, TParams = void> {
  /** Table name to query */
  tableName: T;

  /** Columns to select */
  columns: string | ((params: TParams) => string);

  /** Generate query key from params */
  queryKey: (params: TParams) => readonly unknown[];

  /**
   * Chain query methods after select (filter, order, limit, single, etc.)
   */
  trailingQuery: (
    query: SelectBuilder<T>,
    params: TParams,
  ) => ReturnType<SelectBuilder<T>['single']>;

  /** Transform Row to Domain type */
  parseFn: (data: TRow) => TDomain;

  /** Stale time in ms (optional, default: 30s) */
  staleTime?: number;
}

// ============ Helpers ============

/**
 * Resolve columns from QueryDef (handles both string and function)
 *
 * @param columns - Static string or function returning column string
 * @param params - Query parameters to pass if columns is a function
 * @returns Resolved column string for SELECT
 *
 * @example
 * ```ts
 * // Static columns
 * resolveColumns('id, name', {}) // => 'id, name'
 *
 * // Dynamic columns based on params
 * resolveColumns(
 *   ({ includeRelations }) => includeRelations ? '*, posts(*)' : '*',
 *   { includeRelations: true }
 * ) // => '*, posts(*)'
 * ```
 */
export function resolveColumns<TParams>(
  columns: string | ((params: TParams) => string),
  params: TParams,
): string {
  return typeof columns === 'function' ? columns(params) : columns;
}
